import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';

function BonusRulesPanel({ user, token }) {
    const [rules, setRules] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        bonus_amount: '',
        bonus_type: 'overtime',
        condition_value: '',
        is_active: true,
        position_filter: 'Все'
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const isOwnerOrAdmin = ['owner', 'admin'].includes(user?.role);

    // Типы бонусов
    const bonusTypes = [
        { value: 'overtime', label: 'Переработка', color: 'warning' },
        { value: 'extra_shift', label: 'Доп. смена', color: 'info' },
        { value: 'excursion', label: 'Экскурсии', color: 'success' },
        { value: 'other', label: 'Другое', color: 'default' }
    ];

    const positions = ['Все', 'Гид-экскурсовод', 'Администратор', 'Повар'];

    useEffect(() => {
        fetchRules();
    }, []);

    const fetchRules = async () => {
        try {
            const response = await fetch(`${API_URL}/api/bonus-rules`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setRules(data.data);
            }
        } catch (error) {
            console.error('Ошибка при загрузке правил:', error);
        }
    };

    const handleOpenDialog = (rule = null) => {
        if (rule) {
            setEditingRule(rule);
            setFormData({
                name: rule.name,
                description: rule.description || '',
                bonus_amount: rule.bonus_amount,
                bonus_type: rule.bonus_type,
                condition_value: rule.condition_value || '',
                is_active: rule.is_active,
                position_filter: rule.position_filter
            });
        } else {
            setEditingRule(null);
            setFormData({
                name: '',
                description: '',
                bonus_amount: '',
                bonus_type: 'overtime',
                condition_value: '',
                is_active: true,
                position_filter: 'Все'
            });
        }
        setOpenDialog(true);
        setError('');
        setSuccess('');
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingRule(null);
        setError('');
        setSuccess('');
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.bonus_amount) {
            setError('Заполните обязательные поля');
            return;
        }

        try {
            const url = editingRule
                ? `${API_URL}/api/bonus-rules/${editingRule.id}`
                : `${API_URL}/api/bonus-rules`;

            const response = await fetch(url, {
                method: editingRule ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (!data.success) {
                setError(data.error);
                return;
            }

            setSuccess(editingRule ? 'Правило обновлено' : 'Правило создано');
            setTimeout(() => {
                handleCloseDialog();
                fetchRules();
            }, 1000);

        } catch (error) {
            setError('Ошибка при сохранении');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Удалить это правило бонуса?')) return;

        try {
            const response = await fetch(`${API_URL}/api/bonus-rules/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();

            if (data.success) {
                setSuccess('Правило удалено');
                fetchRules();
            } else {
                alert(data.error);
            }
        } catch (error) {
            alert('Ошибка при удалении');
        }
    };

    const getBonusTypeLabel = (type) => {
        return bonusTypes.find(t => t.value === type)?.label || type;
    };

    const getBonusTypeColor = (type) => {
        return bonusTypes.find(t => t.value === type)?.color || 'default';
    };

    // Группировка правил по типам
    const groupedRules = rules.reduce((acc, rule) => {
        if (!acc[rule.bonus_type]) {
            acc[rule.bonus_type] = [];
        }
        acc[rule.bonus_type].push(rule);
        return acc;
    }, {});

    // Статистика
    const stats = {
        total: rules.length,
        active: rules.filter(r => r.is_active).length,
        total_amount: rules.filter(r => r.is_active).reduce((sum, r) => sum + parseFloat(r.bonus_amount), 0)
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                <Typography variant="h4">
                    <MonetizationOnIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Правила начисления бонусов
                </Typography>
                {isOwnerOrAdmin && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Добавить правило
                    </Button>
                )}
            </Box>

            {/* Статистика */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Typography color="text.secondary" variant="body2">
                                Всего правил
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                                {stats.total}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Typography color="text.secondary" variant="body2">
                                Активных правил
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1, color: 'success.main' }}>
                                {stats.active}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Typography color="text.secondary" variant="body2">
                                Макс. бонус за смену
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1, color: 'primary.main' }}>
                                {Math.max(...rules.filter(r => r.is_active).map(r => parseFloat(r.bonus_amount)), 0).toLocaleString()} ₽
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {/* Правила по категориям */}
            {Object.keys(groupedRules).map((type) => (
                <Paper key={type} sx={{ mb: 3, p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        <Chip
                            label={getBonusTypeLabel(type)}
                            color={getBonusTypeColor(type)}
                            sx={{ mr: 1 }}
                        />
                        ({groupedRules[type].length})
                    </Typography>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.100' }}>
                                    <TableCell><strong>Название</strong></TableCell>
                                    <TableCell><strong>Условие</strong></TableCell>
                                    <TableCell><strong>Сумма</strong></TableCell>
                                    <TableCell><strong>Должность</strong></TableCell>
                                    <TableCell><strong>Статус</strong></TableCell>
                                    {isOwnerOrAdmin && <TableCell><strong>Действия</strong></TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groupedRules[type].map((rule) => (
                                    <TableRow key={rule.id} hover>
                                        <TableCell>
                                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                {rule.name}
                                            </Typography>
                                            {rule.description && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {rule.description}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={rule.condition_value || '—'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="h6" color="success.main">
                                                +{parseFloat(rule.bonus_amount).toLocaleString()} ₽
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={rule.position_filter} size="small" />
                                        </TableCell>
                                        <TableCell>
                                            {rule.is_active ? (
                                                <Chip label="Активно" color="success" size="small" />
                                            ) : (
                                                <Chip label="Неактивно" color="default" size="small" />
                                            )}
                                        </TableCell>
                                        {isOwnerOrAdmin && (
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleOpenDialog(rule)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDelete(rule.id)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            ))}

            {rules.length === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                        Правила бонусов не созданы
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Добавьте первое правило для автоматического расчета бонусов сотрудникам
                    </Typography>
                </Paper>
            )}

            {/* Диалог добавления/редактирования */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingRule ? 'Редактировать правило' : 'Новое правило бонуса'}
                </DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                    <TextField
                        fullWidth
                        label="Название правила *"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        margin="normal"
                        placeholder="Например: Переработка >1 час"
                    />

                    <TextField
                        fullWidth
                        label="Описание"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        margin="normal"
                        multiline
                        rows={2}
                        placeholder="Подробное описание условия"
                    />

                    <FormControl fullWidth margin="normal">
                        <InputLabel>Тип бонуса *</InputLabel>
                        <Select
                            value={formData.bonus_type}
                            onChange={(e) => setFormData({ ...formData, bonus_type: e.target.value })}
                        >
                            {bonusTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        label="Условие"
                        value={formData.condition_value}
                        onChange={(e) => setFormData({ ...formData, condition_value: e.target.value })}
                        margin="normal"
                        placeholder="Например: >1hour, >=5"
                        helperText="Условие срабатывания бонуса"
                    />

                    <TextField
                        fullWidth
                        label="Сумма бонуса (₽) *"
                        type="number"
                        value={formData.bonus_amount}
                        onChange={(e) => setFormData({ ...formData, bonus_amount: e.target.value })}
                        margin="normal"
                        inputProps={{ min: 0, step: 50 }}
                    />

                    <FormControl fullWidth margin="normal">
                        <InputLabel>Должность</InputLabel>
                        <Select
                            value={formData.position_filter}
                            onChange={(e) => setFormData({ ...formData, position_filter: e.target.value })}
                        >
                            {positions.map((pos) => (
                                <MenuItem key={pos} value={pos}>
                                    {pos}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            />
                        }
                        label="Активно"
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Отмена</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={!formData.name || !formData.bonus_amount}
                    >
                        {editingRule ? 'Сохранить' : 'Создать'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default BonusRulesPanel;
